// ======================================================================
// FILE: src/modules/wms/sla/pages/SLAPolicyFormPage.js
// MODULE: Work Management System (WMS)
// FEATURE: SLA Management
//
// PURPOSE:
//   Create / Edit SLA Policy
//   Manage SLA Rules
//   Single Master Screen
//
// ROUTES:
//   /wms/sla-policies/create
//   /wms/sla-policies/:id/edit
//
// DEPENDENCIES:
//   wmsSLAService
//   SLARulesTab
//
// ARCHITECTURE:
//   ✓ TaskFlow ERP Standard
//   ✓ AdminLTE
//   ✓ Multi Tenant
//   ✓ Multi Location
//   ✓ Audit Enabled
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import wmsSLAService
from "../../services/wmsSLAService";

import wmsCalendarService
from "../../services/wmsCalendarService";

import SLARulesTab
from "../components/SLARulesTab";

export default function SLAPolicyFormPage() {

  const navigate = useNavigate();

  const { id } = useParams();

  const isEdit = !!id;

  const [saving, setSaving] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [calendars, setCalendars] =
    useState([]);

    const [shifts, setShifts] =
    useState([]);  

  const [form, setForm] =
    useState({

      sla_policy_code: "",
      sla_policy_name: "",

      calendar_id: "",
      shift_id: "",

      applicable_work_item_type:
        "TASK",

      description: "",
      remarks: "",
    });

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    loadCalendars();

    }, []);

  useEffect(() => {

    if (!isEdit) return;

    loadPolicy();

  }, [id]);

  useEffect(() => {

  if (
    form.calendar_id
  ) {

    loadShifts(
      form.calendar_id
    );
  }

}, [form.calendar_id]);
  
  async function loadPolicy() {

    try {

      setLoading(true);

      const data =
        await wmsSLAService.getSLAPolicy(id);

      setForm({

        sla_policy_code:
          data.sla_policy_code || "",

        sla_policy_name:
          data.sla_policy_name || "",

        calendar_id:
          data.calendar_id || "",

        shift_id:
          data.shift_id || "",

        applicable_work_item_type:
          data.applicable_work_item_type
          || "TASK",

        description:
          data.description || "",

        remarks:
          data.remarks || "",
      });

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load SLA Policy"
      );

    } finally {

      setLoading(false);
    }
  }

  // ==========================================================
  // CHANGE
  // ==========================================================

  // ==========================================================
    // CALENDARS
    // ==========================================================

    async function loadCalendars() {

    try {

        const result =
        await wmsCalendarService
            .listCalendars({
            page: 1,
            per_page: 1000,
            });

        setCalendars(
        result.items || []
        );

    } catch (err) {

        console.error(err);
    }
    }

    // ==========================================================
    // SHIFTS
    // ==========================================================

    async function loadShifts(
    calendarId
    ) {

    if (!calendarId) {

        setShifts([]);

        return;
    }

    try {

        const data =
        await wmsCalendarService
            .getShifts(
            calendarId
            );

        setShifts(
        data || []
        );

    } catch (err) {

        console.error(err);

        setShifts([]);
    }
    }
  
  
  function handleChange(
    field,
    value
  ) {

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  // ==========================================================
  // VALIDATION
  // ==========================================================

  function validate() {

    if (
      !form.sla_policy_code?.trim()
    ) {

      alert(
        "SLA Policy Code is required"
      );

      return false;
    }

    if (
      !form.sla_policy_name?.trim()
    ) {

      alert(
        "SLA Policy Name is required"
      );

      return false;
    }

    if (
      !form.calendar_id
    ) {

      alert(
        "Calendar is required"
      );

      return false;
    }

    if (
      !form.applicable_work_item_type
    ) {

      alert(
        "Work Item Type is required"
      );

      return false;
    }

    return true;
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!validate()) {
      return;
    }

    try {

      setSaving(true);

      const payload = {

        sla_policy_code:
          form.sla_policy_code,

        sla_policy_name:
          form.sla_policy_name,

        calendar_id:
          form.calendar_id,

        shift_id:
          form.shift_id || null,

        applicable_work_item_type:
          form.applicable_work_item_type,

        description:
          form.description,

        remarks:
          form.remarks,
      };

      if (isEdit) {

        await wmsSLAService.updateSLAPolicy(
          id,
          payload
        );

      } else {

        await wmsSLAService.createSLAPolicy(
          payload
        );
      }

      navigate(
        "/wms/sla-policies"
      );

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );

    } finally {

      setSaving(false);
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  if (loading) {

    return (

      <div className="container-fluid">

        Loading...

      </div>
    );
  }

  return (

    <div className="container-fluid">

      {/* ==================================================== */}
      {/* HEADER */}
      {/* ==================================================== */}

      <div
        className="card border-0 mb-3"
        style={{
          background:
            "linear-gradient(90deg,#0d6efd,#4f8cff)",
          color: "#fff",
        }}
      >

        <div className="card-body">

          <h3 className="mb-1">

            {isEdit
              ? "Edit SLA Policy"
              : "Create SLA Policy"}

          </h3>

          <div>

            Configure SLA definitions
            and business rules

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* POLICY DETAILS */}
      {/* ==================================================== */}

      <div className="card">

        <div className="card-header">

          <strong>
            SLA Policy Details
          </strong>

        </div>

        <div className="card-body">

          <div className="row">

            {/* CODE */}

            <div className="col-md-3 mb-3">

              <label>
                SLA Code *
              </label>

              <input
                className="form-control"
                value={
                  form.sla_policy_code
                }
                onChange={(e) =>
                  handleChange(
                    "sla_policy_code",
                    e.target.value
                  )
                }
              />

            </div>

            {/* NAME */}

            <div className="col-md-5 mb-3">

              <label>
                SLA Name *
              </label>

              <input
                className="form-control"
                value={
                  form.sla_policy_name
                }
                onChange={(e) =>
                  handleChange(
                    "sla_policy_name",
                    e.target.value
                  )
                }
              />

            </div>

            {/* WORK ITEM TYPE */}

            <div className="col-md-4 mb-3">

              <label>
                Work Item Type *
              </label>

              <select
                className="form-control"
                value={
                  form.applicable_work_item_type
                }
                onChange={(e) =>
                  handleChange(
                    "applicable_work_item_type",
                    e.target.value
                  )
                }
              >

                <option value="TASK">
                  Task
                </option>

                <option value="AUDIT">
                  Audit
                </option>

                <option value="OBSERVATION">
                  Observation
                </option>

                <option value="CAPA">
                  CAPA
                </option>

              </select>

            </div>

            {/* CALENDAR */}

            <div className="col-md-6 mb-3">

              <label>
                Calendar *
              </label>

              <select
                className="form-control"
                value={
                    form.calendar_id
                }
                onChange={(e) => {

                    handleChange(
                    "calendar_id",
                    e.target.value
                    );

                    handleChange(
                    "shift_id",
                    ""
                    );
                }}
                >

                <option value="">
                    Select Calendar
                </option>

                {calendars.map(
                    (calendar) => (

                    <option
                        key={calendar.id}
                        value={calendar.id}
                    >
                        {
                        calendar.calendar_name
                        }
                    </option>

                    )
                )}

                </select>

            </div>

            {/* SHIFT */}

            <div className="col-md-6 mb-3">

              <label>
                Shift
              </label>

              <select
                className="form-control"
                value={
                    form.shift_id
                }
                onChange={(e) =>
                    handleChange(
                    "shift_id",
                    e.target.value
                    )
                }
                >

                <option value="">
                    Select Shift
                </option>

                {shifts.map(
                    (shift) => (

                    <option
                        key={shift.id}
                        value={shift.id}
                    >
                        {
                        shift.shift_name
                        }
                    </option>

                    )
                )}

                </select>

            </div>

            {/* DESCRIPTION */}

            <div className="col-md-12 mb-3">

              <label>
                Description
              </label>

              <textarea
                rows="3"
                className="form-control"
                value={
                  form.description
                }
                onChange={(e) =>
                  handleChange(
                    "description",
                    e.target.value
                  )
                }
              />

            </div>

            {/* REMARKS */}

            <div className="col-md-12 mb-3">

              <label>
                Remarks
              </label>

              <textarea
                rows="3"
                className="form-control"
                value={
                  form.remarks
                }
                onChange={(e) =>
                  handleChange(
                    "remarks",
                    e.target.value
                  )
                }
              />

            </div>

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* RULES */}
      {/* ==================================================== */}

      <SLARulesTab
        policyId={id}
        isEdit={isEdit}
      />

      {/* ==================================================== */}
      {/* FOOTER */}
      {/* ==================================================== */}

      <div className="card mt-3">

        <div className="card-body text-end">

          <button
            className="btn btn-primary me-2"
            onClick={handleSave}
            disabled={saving}
          >

            {saving
              ? "Saving..."
              : "Save"}

          </button>

          <button
            className="btn btn-secondary"
            onClick={() =>
              navigate(
                "/wms/sla-policies"
              )
            }
          >
            Cancel
          </button>

        </div>

      </div>

    </div>
  );
}